/**
 * App-side schema barrel — after stage 2.5 it carries exactly three kinds of exports:
 *
 * 1. STRICT tightened re-exports (Content CMS, Product*, Order*, PaymentRequest): the
 *    curated bases in `@vs-shop/schemas/branded/*` deliberately leave large generated
 *    closures as documented override slots; the app-side `*Tightened` modules re-apply
 *    the exact generated roots so every name exported here decodes byte-identically to
 *    the pre-2.5 behavior. Consumers of these names MUST import them from this barrel
 *    (or the tightened modules), never from `@vs-shop/schemas` directly.
 * 2. RAW generated aliases (page blocks, cart mutations, search/homepage serializations):
 *    consumed by app-side components/pages only. Their curation is deferred — the MR-D
 *    census decides per consumer whether it moves engine-side (then needs a curated
 *    counterpart) or stays app-side (may keep importing gen legally).
 * 3. Nothing else: fully-curated slot-free schemas moved to `@vs-shop/schemas` and their
 *    consumers were repointed to import the package directly (stage-2.5d codemod).
 *
 * The strict-vs-package split collapses once the MR-D tenant-schema-injection seam
 * exists (workspace#46 note, relates to out-of-scope #30).
 */

import type { Schema } from 'effect';

import type { components } from '../../../types/openapi';

import type {
  AddToCartButtonElementDTOjsonldsyliusshoppageshow as AddToCartButtonBlockGenerated,
  AdvantagesElementDTOjsonldsyliusshoppageshow as AdvantagesBlockGenerated,
  BaseButtonElementDTOjsonldsyliusshoppageshow as BaseButtonBlockGenerated,
  ButtonGridElementDTOjsonldsyliusshoppageshow as ButtonGridBlockGenerated,
  ButtonGridItemDTOjsonldsyliusshoppageshow,
  GoogleMapsElementDTOjsonldsyliusshoppageshow as GoogleMapsBlockGenerated,
  ImageCollectionElementDTOsyliusshoppageshow as ImageCollectionBlockGenerated,
  ImageElementDTOjsonldsyliusshoppageshow as ImageBlockGenerated,
  ImagePairElementDTOjsonldsyliusshoppageshow as ImagePairBlockGenerated,
  MarkdownPerexElementDTOjsonldsyliusshoparticleshow as MarkdownPerexBlockGenerated,
  MarkdownTextElementDTOjsonldsyliusshoppageshow as MarkdownTextBlockGenerated,
  MarkdownTextWithImageElementDTOjsonldsyliusshoppageshow as MarkdownTextWithImageBlockGenerated,
  MarkdownThreeColumnsElementDTOsyliusshoppageshow as MarkdownThreeColumnsBlockGenerated,
  MarkdownTwoColumnsElementDTOjsonldsyliusshoppageshow as MarkdownTwoColumnsBlockGenerated,
  OrderAddItemToCartjsonldsyliusshopcartadditem,
  ProductElementDTOjsonldsyliusshoppageshow as ProductBlockGenerated,
  ProductListElementDTOjsonldsyliusshoppageshow as ProductListGenerated,
  QuoteElementDTOjsonldsyliusshoppageshow as QuoteBlockGenerated,
  RetinoWidgetElementDTOjsonldsyliusshoppageshow as RetinoWidgetBlockGenerated,
  TableElementDTOjsonldsyliusshoppageshow,
  TeamElementDTOjsonldsyliusshoppageshow as TeamBlockGenerated,
  TitleElementDTOjsonldsyliusshoppageshow as TitleBlockGenerated,
  WhiteBoxElementDTOjsonldsyliusshoppageshow as WhiteBoxBlockGenerated,
  YoutubeElementDTOjsonldsyliusshoppageshow as YoutubeBlockGenerated,
} from '../gen/rcprofi';
import {
  Addressjsonldsyliusshopaddresscreate,
  Addressjsonldsyliusshopaddressupdate,
  AlertMessagejsonldsyliusshopmessageindex,
  Articlejsonldsyliusshophomepageshow,
  AutomaticAccessoryItem,
  Bannerjsonldsyliusshophomepageshow,
  BrandAlphabetjsonldshopbrandshowalphabet,
  Brandjsonldsyliusshopbrandindex,
  Brandjsonldsyliusshopbrandindexdetail,
  Brandjsonldsyliusshopbrandshow,
  Brandjsonldsyliusshopbrandshowdetail,
  Brandjsonldsyliusshophomepageshow,
  Brandsyliusshopproductshowsyliusshopproductshowdetail,
  CartPromotionRuleHintsjsonldsyliusshopcartshowsyliusshopcartshowdetail,
  ConstraintViolation as ConstraintViolationGenerated,
  ConstraintViolationjsonld as ConstraintViolationjsonldGenerated,
  Customerjsonldsyliusshopcustomerupdate,
  Customershopcustomertokenread,
  Error as ErrorGenerated,
  HeurekaReviewjsonldsyliusshophomepageshow,
  HomepageDTOjsonldsyliusshophomepageshow,
  Labeljsonldsyliusshopproductvariantshowdetail,
  NewsletterSubscribeInputjsonld,
  Orderjsonldsyliusshopcartshow,
  Orderjsonldsyliusshoporderaccountshow,
  OrderUpdateCartjsonldsyliusshopcartupdate,
  PaymentOptionsyliusshopshippingandpaymentoptionsindex,
  PriceHistorysyliusshopproductvariantshowpricehistory,
  ProductAttributeGroupjsonldsyliusshopproductattributegroupindex,
  ProductAttributeValuejsonldsyliusshopproductshowsyliusshopproductshowdetail,
  ProductDocumentjsonldsyliusshopcartshowsyliusshopcartshowdetailsyliusshopcartshowpaid,
  ProductImagesyliusshopproductshowsyliusshopproductshowdetail,
  Productjsonldsyliusshoppageshow,
  Productjsonldsyliusshopproductindexbase,
  Productjsonldsyliusshopproductshow,
  ProductVariantjsonldsyliusshopproductvariantindex,
  ProductVariantjsonldsyliusshopproductvariantshow,
  ProductVideosyliusshopproductshowsyliusshopproductshowdetail,
  ShippingOptionsjsonldshopshippingAndPaymentOptionsindex,
  SuggestHitjsonld,
  SuggestResultjsonld,
  TaxonImagejsonldsyliusshoptaxonmenu,
  TaxonImagesyliusshoptaxonshow,
  Taxonjsonldsyliusshophomepageshow,
  Taxonjsonldsyliusshoptaxonindex,
  Taxonjsonldsyliusshoptaxonindexshort,
  Taxonjsonldsyliusshoptaxonmenu,
  Taxonjsonldsyliusshoptaxonshowdetail,
  UrlMatchjsonldroutershow,
} from '../gen/rcprofi';

// Product's package bases leave its generated nested closure loose. Re-export
// the app-tightened names here so the established backend surface stays exact.
import { Product, ProductBase, ProductVariant } from '../branded/productTightened';
// The order package base leaves the generated cart closure loose. Re-export
// RcProfi's generated-root tightening so this established surface stays exact.
import { OrderDetail, OrderDetailPaid, OrderIndex } from '../branded/orderTightened';
import { PaymentRequest as PaymentRequestSchema } from '../branded/paymentRequestTightened';
// Branded curated customer layer (WS7 Task 6); see `effect/schema/branded/CustomerSchema`.
import { CustomerMe, CustomerShow } from '@vs-shop/schemas/branded/CustomerSchema';
import {
  ArticleShow,
  FaqQuestionShow,
  GlossaryTermShow,
  PageShow,
} from '../branded/contentTightened';
// Branded content-entity layer (WS7 Task 7a); see `effect/schema/branded/ContentSchema`.
import {
  ArticleAuthorIndex,
  ArticleAuthorIndexFavourite,
  ArticleAuthorShow,
  ArticleAuthorShowFromArticleDetail,
  ArticleCategoryIndex,
  ArticleCategoryShow,
  ArticleIndex,
  ArticleIndexFavourite,
  ArticleTagShow,
  AuthorIndex,
  SectionIndex,
  TagIndex,
} from '@vs-shop/schemas/branded/ContentSchema';
// Branded Faq / Banner / Address / FavouriteList layer (WS7 Task 7b);
// see `effect/schema/branded/MiscSchema`.
import {
  AddressShow,
  Banner,
  FaqTopicDetail,
  FaqTopicIndex,
  ProductFavouriteListCreateResponse,
  ProductFavouriteListWithProductCodes,
  ProductFavouriteListWithoutProducts,
} from '@vs-shop/schemas/branded/MiscSchema';

// Article schemas — the content-entity serializations carry the WS7 branded
// field types (see `effect/schema/branded/ContentSchema`), re-exported here as
// the single surface.
export {
  ArticleAuthorIndex,
  ArticleAuthorIndexFavourite,
  ArticleAuthorShow,
  ArticleAuthorShowFromArticleDetail,
  ArticleCategoryIndex,
  ArticleCategoryShow,
  ArticleIndex,
  ArticleIndexFavourite,
  ArticleShow,
  ArticleTagShow,
  AuthorIndex,
  SectionIndex,
  TagIndex,
};

// Banner schemas — branded (WS7 Task 7b); see `effect/schema/branded/MiscSchema`.
export { Banner };
export type BannerPlacement = Banner['placement'][number];

// Homepage schemas
export const HomepageShow = HomepageDTOjsonldsyliusshophomepageshow;
export type HomepageShow = HomepageDTOjsonldsyliusshophomepageshow;

export const HomepageBrand = Brandjsonldsyliusshophomepageshow;
export type HomepageBrand = Brandjsonldsyliusshophomepageshow;

export const HomepageBanner = Bannerjsonldsyliusshophomepageshow;
export type HomepageBanner = Bannerjsonldsyliusshophomepageshow;

export const HomepageArticle = Articlejsonldsyliusshophomepageshow;
export type HomepageArticle = Articlejsonldsyliusshophomepageshow;

export const HomepageTaxon = Taxonjsonldsyliusshophomepageshow;
export type HomepageTaxon = Taxonjsonldsyliusshophomepageshow;

export const HomepageHeurekaReview = HeurekaReviewjsonldsyliusshophomepageshow;
export type HomepageHeurekaReview = HeurekaReviewjsonldsyliusshophomepageshow;

// Brand schemas
export const BrandAlphabet = BrandAlphabetjsonldshopbrandshowalphabet;
export type BrandAlphabet = BrandAlphabetjsonldshopbrandshowalphabet;

export const BrandIndex = Brandjsonldsyliusshopbrandindex;
export type BrandIndex = Brandjsonldsyliusshopbrandindex;

export const BrandIndexDetail = Brandjsonldsyliusshopbrandindexdetail;
export type BrandIndexDetail = Brandjsonldsyliusshopbrandindexdetail;

export const BrandShow = Brandjsonldsyliusshopbrandshow;
export type BrandShow = Brandjsonldsyliusshopbrandshow;

export const BrandDetail = Brandjsonldsyliusshopbrandshowdetail;
export type BrandDetail = Brandjsonldsyliusshopbrandshowdetail;

export const ProductBrand = Brandsyliusshopproductshowsyliusshopproductshowdetail;
export type ProductBrand = Brandsyliusshopproductshowsyliusshopproductshowdetail;

// Article content blocks (type-only exports - already well-named)
export type ContentBlock = PageShow['content'][number] | ArticleShow['content'][number];
export type AddToCartButtonBlock = AddToCartButtonBlockGenerated;
export type AdvantagesBlock = AdvantagesBlockGenerated;
export type BaseButtonBlock = BaseButtonBlockGenerated;
export type ButtonGridBlock = ButtonGridBlockGenerated;
export type GoogleMapsBlock = GoogleMapsBlockGenerated;
export type ImageBlock = ImageBlockGenerated;
export type ImageCollectionBlock = ImageCollectionBlockGenerated;
export type ImagePairBlock = ImagePairBlockGenerated;
export type MarkdownPerexBlock = MarkdownPerexBlockGenerated;
export type MarkdownTextBlock = MarkdownTextBlockGenerated;
export type MarkdownTextWithImageBlock = MarkdownTextWithImageBlockGenerated;
export type MarkdownThreeColumnsBlock = MarkdownThreeColumnsBlockGenerated;
export type MarkdownTwoColumnsBlock = MarkdownTwoColumnsBlockGenerated;
export type ProductBlock = ProductBlockGenerated;
export type ProductListBlock = ProductListGenerated;
export type QuoteBlock = QuoteBlockGenerated;
export type RetinoWidgetBlock = RetinoWidgetBlockGenerated;
export type TeamBlock = TeamBlockGenerated;
export type TitleBlock = TitleBlockGenerated;
export type WhiteBoxBlock = WhiteBoxBlockGenerated;
export type YoutubeBlock = YoutubeBlockGenerated;
export type ButtonGridBlockItem = ButtonGridItemDTOjsonldsyliusshoppageshow;
export type TableBlock = TableElementDTOjsonldsyliusshoppageshow;

// Constraint violation schemas (already well-named)
export const ConstraintViolation = ConstraintViolationGenerated;
export type ConstraintViolation = ConstraintViolationGenerated;

export const ConstraintViolationjsonld = ConstraintViolationjsonldGenerated;
export type ConstraintViolationjsonld = ConstraintViolationjsonldGenerated;

// RFC 7807 problem+json error schema
export const ApiError = ErrorGenerated;
export type ApiError = ErrorGenerated;

// Customer schemas
// CustomerMe / CustomerShow carry the WS7 branded field types (see
// `effect/schema/branded/CustomerSchema`), re-exported here as the single surface.
export { CustomerMe, CustomerShow };

export const CustomerTokenRead = Customershopcustomertokenread;
export type CustomerTokenRead = Customershopcustomertokenread;

export const CustomerUpdateBaseInfo = Customerjsonldsyliusshopcustomerupdate;
export type CustomerUpdateBaseInfo =
  components['schemas']['Customer.jsonld-sylius.shop.customer.update'];

// Message schemas
export const AlertMessageIndex = AlertMessagejsonldsyliusshopmessageindex;
export type AlertMessageIndex = AlertMessagejsonldsyliusshopmessageindex;

// Newsletter schemas
export const NewsletterSubscribeInput = NewsletterSubscribeInputjsonld;
export type NewsletterSubscribeInput = NewsletterSubscribeInputjsonld;

// Order schemas
export const CartPromotionRuleHints =
  CartPromotionRuleHintsjsonldsyliusshopcartshowsyliusshopcartshowdetail;
export type CartPromotionRuleHints =
  CartPromotionRuleHintsjsonldsyliusshopcartshowsyliusshopcartshowdetail;

export type AddProductVariantToOrder = OrderAddItemToCartjsonldsyliusshopcartadditem;

export const OrderUpdateCart = OrderUpdateCartjsonldsyliusshopcartupdate;
export type OrderUpdateCart =
  components['schemas']['Order.UpdateCart.jsonld-sylius.shop.cart.update'];

export const OrderShow = Orderjsonldsyliusshopcartshow;
export type OrderShow = Orderjsonldsyliusshopcartshow;

export const OrderAccountShow = Orderjsonldsyliusshoporderaccountshow;
export type OrderAccountShow = Orderjsonldsyliusshoporderaccountshow;

// OrderIndex carries the generated-root strict branded fields and is re-exported here so the curated
// order layer remains the single import surface.
export { OrderIndex };
export type OrderGlobalStateT = OrderIndex['globalState'];
// Raw (unbranded) form of `globalState` — brands live on the `Type` side only,
// so the `Encoded` side keeps the plain literal union. Use for literal tables
// and `Match.when` patterns that need raw literals.
export type OrderGlobalStateEncodedT = Schema.Codec.Encoded<typeof OrderIndex>['globalState'];

// Page schemas
// Branded (WS7 Task 8); see `effect/schema/branded/MiscSchema`.
export { PageShow };

// Product schemas
export const ProductAttributeGroupIndex =
  ProductAttributeGroupjsonldsyliusshopproductattributegroupindex;
export type ProductAttributeGroupIndex =
  ProductAttributeGroupjsonldsyliusshopproductattributegroupindex;

export const ProductAttributeValue =
  ProductAttributeValuejsonldsyliusshopproductshowsyliusshopproductshowdetail;
export type ProductAttributeValue =
  ProductAttributeValuejsonldsyliusshopproductshowsyliusshopproductshowdetail;

export const ProductImage = ProductImagesyliusshopproductshowsyliusshopproductshowdetail;
export type ProductImage = ProductImagesyliusshopproductshowsyliusshopproductshowdetail;

export const LabelProductVariantShow = Labeljsonldsyliusshopproductvariantshowdetail;
export type LabelProductVariantShow = Labeljsonldsyliusshopproductvariantshowdetail;

export const ProductShow = Productjsonldsyliusshopproductshow;
export type ProductShow = Productjsonldsyliusshopproductshow;

export const ProductPageShow = Productjsonldsyliusshoppageshow;
export type ProductPageShow = Productjsonldsyliusshoppageshow;

export const ProductVariantShow = ProductVariantjsonldsyliusshopproductvariantshow;
export type ProductVariantShow = ProductVariantjsonldsyliusshopproductvariantshow;

export const ProductVideo = ProductVideosyliusshopproductshowsyliusshopproductshowdetail;
export type ProductVideo = ProductVideosyliusshopproductshowsyliusshopproductshowdetail;

// Shipping and Payment schemas
export const PaymentOptions = PaymentOptionsyliusshopshippingandpaymentoptionsindex;
export type PaymentOptions = PaymentOptionsyliusshopshippingandpaymentoptionsindex;

export const PaymentRequest = PaymentRequestSchema;
export type PaymentRequest = Schema.Schema.Type<typeof PaymentRequest>;

export const ShippingOptions = ShippingOptionsjsonldshopshippingAndPaymentOptionsindex;
export type ShippingOptions = ShippingOptionsjsonldshopshippingAndPaymentOptionsindex;

// Taxon schemas
export const TaxonImageMenu = TaxonImagejsonldsyliusshoptaxonmenu;
export type TaxonImageMenu = TaxonImagejsonldsyliusshoptaxonmenu;

export const TaxonImageShow = TaxonImagesyliusshoptaxonshow;
export type TaxonImageShow = TaxonImagesyliusshoptaxonshow;

export const TaxonIndex = Taxonjsonldsyliusshoptaxonindex;
export type TaxonIndex = Taxonjsonldsyliusshoptaxonindex;

export const TaxonMenu = Taxonjsonldsyliusshoptaxonmenu;
export type TaxonMenu = Taxonjsonldsyliusshoptaxonmenu;

export const TaxonShowDetail = Taxonjsonldsyliusshoptaxonshowdetail;
export type TaxonShowDetail = Taxonjsonldsyliusshoptaxonshowdetail;

export const TaxonAssociation = Taxonjsonldsyliusshoptaxonindexshort;
export type TaxonAssociation = Taxonjsonldsyliusshoptaxonindexshort;

// URL matching schemas
export const UrlMatchShow = UrlMatchjsonldroutershow;
export type UrlMatchShow = UrlMatchjsonldroutershow;

// Faq schemas
// Branded (WS7 Task 7b); see `effect/schema/branded/MiscSchema`.
export { FaqQuestionShow, FaqTopicDetail, FaqTopicIndex };

// Glossary schemas
// The package base leaves `content` loose; the app tightening restores the
// exact generated element closure — see `effect/schema/branded/contentTightened`.
export { GlossaryTermShow };
export type GlossaryContentBlock = GlossaryTermShow['content'][number];

// Search suggest schemas (endpoint serves application/ld+json → jsonld variant)
export const SuggestResult = SuggestResultjsonld;
export type SuggestResult = SuggestResultjsonld;

export const SuggestHit = SuggestHitjsonld;
export type SuggestHit = SuggestHitjsonld;

// --------------------------------------------------------------------------------------------------------------------

// Product / ProductBase / ProductVariant carry the WS7 branded field types
// (see `effect/schema/branded/ProductSchema`) and are re-exported here so the
// curated product layer remains the single import surface.
export { Product, ProductBase, ProductVariant };

export type ProductVariantStockStatus = ProductVariant['stockStatus'];

export const ProductVariantPriceHistoryItem = PriceHistorysyliusshopproductvariantshowpricehistory;
export type ProductVariantPriceHistoryItem = PriceHistorysyliusshopproductvariantshowpricehistory;

// ProductBase is branded + re-exported in the block above.

export const ProductAutomaticAccessory = AutomaticAccessoryItem;
export type ProductAutomaticAccessory = AutomaticAccessoryItem;

export const ProductBaseIndex = Productjsonldsyliusshopproductindexbase;
export type ProductBaseIndex = Productjsonldsyliusshopproductindexbase;

export const ProductVariantIndex = ProductVariantjsonldsyliusshopproductvariantindex;
export type ProductVariantIndex = ProductVariantjsonldsyliusshopproductvariantindex;

// OrderDetail / OrderDetailPaid carry the generated-root strict branded field
// types (see `effect/schema/branded/orderTightened`), re-exported here as the
// single surface.
export { OrderDetail, OrderDetailPaid };

export const OrderDocument =
  ProductDocumentjsonldsyliusshopcartshowsyliusshopcartshowdetailsyliusshopcartshowpaid;
export type OrderDocument =
  ProductDocumentjsonldsyliusshopcartshowsyliusshopcartshowdetailsyliusshopcartshowpaid;

// Alias of the branded ArticleIndexFavourite (WS7 Task 7a).
export const RelatedArticle = ArticleIndexFavourite;
export type RelatedArticle = ArticleIndexFavourite;

// Address
// Branded (WS7 Task 7b); see `effect/schema/branded/MiscSchema`.
export { AddressShow };

export const AddressUpdate = Addressjsonldsyliusshopaddressupdate;
export type AddressUpdate = components['schemas']['Address.jsonld-sylius.shop.address.update'];

export const AddressCreate = Addressjsonldsyliusshopaddresscreate;
export type AddressCreate = components['schemas']['Address.jsonld-sylius.shop.address.create'];

// Product Favourite Lists
// Branded (WS7 Task 7b); see `effect/schema/branded/MiscSchema`.
export {
  ProductFavouriteListCreateResponse,
  ProductFavouriteListWithProductCodes,
  ProductFavouriteListWithoutProducts,
};

export type ProductFavouriteListModifyProductsInputProductVariantCodes =
  components['schemas']['ProductFavouriteList.ModifyFavouriteListProductsInput']['productVariantCodes'];
